import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Chip, CircularProgress, useTheme } from '@mui/material';
import { InfoOutlined, Event, LocationOn } from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { sk } from 'date-fns/locale';
import eventsService from '../services/events.service';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';
import EventDetailDrawer from './EventDetailDrawer';

const EventsActivity = () => {
    const theme = useTheme();
    const { isDarkMode } = usePreferences();
    const colors = getThemeColors(isDarkMode);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerTarget = useRef(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const loadEvents = useCallback(async (after = null, append = false) => {
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await eventsService.getEventsActivity(20, after);
            const responseData = response.data;
            
            let eventsData = [];
            let nextCursorValue = null;

            if (Array.isArray(responseData)) {
                eventsData = responseData;
                nextCursorValue = null;
            } else if (responseData && responseData.data) {
                eventsData = responseData.data;
                nextCursorValue = responseData.paging?.next_cursor || null;
            }

            if (append) {
                setEvents(prev => [...prev, ...eventsData]);
            } else {
                setEvents(eventsData);
            }

            setNextCursor(nextCursorValue);
            setHasMore(nextCursorValue !== null);
        } catch (error) {
            console.error("Failed to fetch events activity", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadEvents(nextCursor, true);
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, nextCursor, loadingMore, loading, loadEvents]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return '#10b981';
            case 'cancelled':
                return '#ef4444';
            case 'scheduled':
                return '#3b82f6';
            default:
                return '#6b7280';
        }
    };

    const formatDate = (date) => {
        if (!date) return null;
        const eventDate = new Date(date);
        if (isToday(eventDate)) {
            return `Dnes ${format(eventDate, 'HH:mm')}`;
        }
        if (isYesterday(eventDate)) {
            return `Včera ${format(eventDate, 'HH:mm')}`;
        }
        return format(eventDate, 'd.M.yyyy HH:mm', { locale: sk });
    };

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedEvent(null);
    };

    return (
        <Paper 
            sx={{ 
                borderRadius: 4, 
                bgcolor: colors.backgroundSecondary,
                color: colors.textPrimary,
                boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: '600px',
                overflow: 'hidden'
            }}
        >
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                    Udalosti
                </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress sx={{ color: colors.info }} />
                    </Box>
                ) : events.length > 0 ? (
                    <Box>
                        {events.map((event) => (
                            <Box 
                                key={event.id} 
                                onClick={() => handleEventClick(event)}
                                sx={{ 
                                    display: 'flex', 
                                    gap: 1.5, 
                                    mb: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                                    border: `1px solid ${colors.border}`,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                    },
                                    transition: 'background-color 0.2s',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                                    <Event sx={{ color: getStatusColor(event.status), fontSize: 20 }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1, color: colors.textPrimary }}>
                                            {event.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: colors.textTertiary, fontSize: '0.7rem' }}>
                                            {formatDate(event.created_at)}
                                        </Typography>
                                    </Box>
                                    {event.description && (
                                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1, fontSize: '0.875rem' }}>
                                            {event.description}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                        <Chip
                                            label={event.status}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                bgcolor: `${getStatusColor(event.status)}20`,
                                                color: getStatusColor(event.status),
                                                border: `1px solid ${getStatusColor(event.status)}40`,
                                            }}
                                        />
                                        {event.start_time && (
                                            <Typography variant="caption" sx={{ color: colors.textTertiary, fontSize: '0.7rem' }}>
                                                {formatDate(event.start_time)}
                                            </Typography>
                                        )}
                                        {event.location && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LocationOn sx={{ fontSize: '0.875rem', color: colors.textTertiary }} />
                                                <Typography variant="caption" sx={{ color: colors.textTertiary, fontSize: '0.7rem' }}>
                                                    {event.location}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Typography variant="caption" sx={{ color: colors.textTertiary, fontSize: '0.7rem', ml: 'auto' }}>
                                            {event.conversation_name}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        {hasMore && (
                            <Box ref={observerTarget} sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                {loadingMore && <CircularProgress size={24} sx={{ color: colors.info }} />}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center', color: colors.textTertiary }}>
                        <InfoOutlined sx={{ fontSize: 48, mb: 2, color: colors.textTertiary }} />
                        <Typography variant="body1" sx={{ color: colors.textTertiary }}>
                            No events found.
                        </Typography>
                    </Box>
                )}
            </Box>
            <EventDetailDrawer
                event={selectedEvent}
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
            />
        </Paper>
    );
};

export default EventsActivity;

